import PropTypes from 'prop-types'
import { Tabs, TabList, Tab, TabPanel } from 'react-tabs'
import { useState, useMemo, useCallback } from 'react'
import DraggableStudent from './DraggableStudent'
import StudentCard from '../Pairings/StudentCard'
import { ReactComponent as ClapIcon } from '../../../assets/images/common/icons/clap-icon.svg'
import { ReactComponent as SortIcon } from '../../../assets/images/common/icons/sort-icon-2.svg'
import { ReactComponent as CheckmarkIcon } from '../../../assets/images/common/icons/checkmark-icon.svg'
import { UncontrolledPopover } from 'reactstrap'
import SearchInput from '../../common/SearchInput'
import { DragOverlay } from '@dnd-kit/core'

import styles from './ManagePairings.module.scss'
import classNames from 'classnames'

const BASE_SORT_OPTIONS = [
  { key: 'lastName', label: 'Last Name (A-Z)' },
  { key: 'age', label: 'Age' },
  { key: 'level', label: 'Skill Level' },
  { key: 'gender', label: 'Gender' },
]

const PairingsStudents = ({ event, draggingId, draggedIds }) => {
  const [tabIndex, setTabIndex] = useState(0)
  const [searchAttendingValue, setSearchAttendingValue] = useState('')
  const [searchNotAttendingValue, setSearchNotAttendingValue] = useState('')
  const [searchWaitlistValue, setSearchWaitlistValue] = useState('')
  const [sortKeys, setSortKeys] = useState(['level'])

  const eventStudents = useMemo(() => (
    event.students ? [...event.students].filter((s) => !draggedIds.includes(s.id) ) : []
  ), [event.students, draggedIds])

  const sortFunction = useCallback((a, b) => {
    for (const key of sortKeys) {
      if (a[key] < b[key]) {
        return -1
      }
      if (a[key] > b[key]) {
        return 1
      }
    }
    return 0
  }, [sortKeys])

  const handleSortKeyChanged = (key) => {
    setSortKeys(prevKeys => prevKeys.includes(key)
      ? prevKeys.filter(value => value !== key)
      : [...prevKeys, key]
    )
  }

  const matchesName = (student, search) => {
    const term = search.toLowerCase()
    return student.firstName.toLowerCase().includes(term) ||
      student.lastName.toLowerCase().includes(term)
  }

  const rosterStudents = useMemo(() => (
    [...eventStudents]
      .filter((s) => s.status === 'confirmed' || s.status === 'created')
      .sort(sortFunction)
  ), [eventStudents, sortFunction])

  const attendingStudentsFiltered = useMemo(() => (
    rosterStudents.filter((std) => std.status === 'confirmed' && matchesName(std, searchAttendingValue))
  ), [rosterStudents, searchAttendingValue])

  const noResponseStudentsFiltered = useMemo(() => (
    rosterStudents.filter((std) => std.status === 'created' && matchesName(std, searchAttendingValue))
  ), [rosterStudents, searchAttendingValue])

  const notAttendingStudents = useMemo(() => (
    [...eventStudents]
      .filter((s) => s.status === 'declined')
      .sort(sortFunction)
  ), [eventStudents, sortFunction])

  const notAttendingStudentsFiltered = useMemo(() => (
    notAttendingStudents.filter((std) => matchesName(std, searchNotAttendingValue))
  ), [notAttendingStudents, searchNotAttendingValue])

  const waitlistedStudents = useMemo(() => (
    [...eventStudents]
      .filter((s) => s.status === 'waitlisted')
      .sort(sortFunction)
  ), [eventStudents, sortFunction])

  const waitlistedStudentsFiltered = useMemo(() => (
    waitlistedStudents.filter((std) => matchesName(std, searchWaitlistValue))
  ), [waitlistedStudents, searchWaitlistValue])

  const searchByTab = [
    [searchAttendingValue, setSearchAttendingValue],
    [searchWaitlistValue, setSearchWaitlistValue],
    [searchNotAttendingValue, setSearchNotAttendingValue],
  ]
  const [currentSearchValue, setCurrentSearchValue] = searchByTab[tabIndex] || searchByTab[0]

  const handleSearchChange = (e) => setCurrentSearchValue(e.target.value)

  const handleTabChange = (index) => {
    setTabIndex(index)
    const isWaitlistTab = event.hasWaitlistEnabled && index === 1
    setSortKeys(isWaitlistTab ? ['waitlistOrder'] : ['level'])
  }

  const sortOptions = event.hasWaitlistEnabled && tabIndex === 1
    ? [{ key: 'waitlistOrder', label: 'Waitlist Order' }, ...BASE_SORT_OPTIONS]
    : BASE_SORT_OPTIONS

  const renderStudents = (students) => students.map((student) => (
    <div key={student.id} className='d-flex align-items-center w-100'>
      <DraggableStudent student={student} draggingId={draggingId} draggedFrom='communityStudents'/>
    </div>
  ))

  return (
    <Tabs selectedIndex={tabIndex} onSelect={handleTabChange} className='h-100'>
      <TabList className={classNames('d-flex justify-content-between px-3 ', styles.tabList)} >
        <Tab className={`${styles.tab} react-tabs__tab`} selectedClassName={styles.activeTab}>Roster ({rosterStudents.length})</Tab>
        { event.hasWaitlistEnabled &&
          <Tab className={`${styles.tab} react-tabs__tab`} selectedClassName={styles.activeTab}>Waitlist ({waitlistedStudents.length})</Tab>
        }
        <Tab className={`${styles.tab} react-tabs__tab`} selectedClassName={styles.activeTab}>Not Attending ({notAttendingStudents.length})</Tab>
      </TabList>
      <div className='d-flex'>
        <div className={classNames(styles.studentsSearchBar, 'mt-2 mx-3')}>
          <SearchInput
            value={currentSearchValue}
            placeholder='Search for Students'
            onChange={handleSearchChange}
          />
        </div>
        <div className={styles.sortIconWrapper} id='sortButton'>
          <SortIcon/>
          {sortKeys.length > 0 && (
            <span className={styles.sortBadge}>{sortKeys.length}</span>
          )}
        </div>
        <UncontrolledPopover
          placement='bottom'
          target='sortButton'
          trigger='legacy'
        >
          <div className={classNames('d-flex flex-column', styles.sortOptionsMenu)}>
            <span className={styles.optionsHeader}>Sort Students by</span>
            {sortOptions.map(({ key, label }) => (
              <span key={key} aria-hidden='true'
                className={classNames(styles.sortOption, sortKeys.includes(key) && styles.activeOption)}
                onClick={() => handleSortKeyChanged(key)}>
                <CheckmarkIcon className={styles.checkmarkIcon}/>
                {label}
              </span>
            ))}
          </div>
        </UncontrolledPopover>
      </div>

      <TabPanel className={classNames('mx-3', styles.tabPanel)} selectedClassName={styles.activeTabPanel}>
        <div className={styles.studentsList}>
          <p className={styles.studentsStatus}>{`Attending (${attendingStudentsFiltered.length})`}</p>
          {attendingStudentsFiltered.length !== 0 || searchAttendingValue ?
            renderStudents(attendingStudentsFiltered) :
            <div className={classNames('d-flex align-items-center', styles.attendingStudentsPlaceholder)}>
              <ClapIcon className={styles.clapIcon}/>
              <p>All Students have been paired!</p>
            </div>
          }
          <p className={styles.studentsStatus}>{`No Response (${noResponseStudentsFiltered.length})`}</p>
          {noResponseStudentsFiltered.length !== 0 || searchAttendingValue ?
            renderStudents(noResponseStudentsFiltered) :
            <p className={styles.noResponseStudentsPlaceholder}>No students left in this category</p>
          }
          <DragOverlay>
            {draggingId !== -1 && <StudentCard dragging student={rosterStudents.find(s => s.id === draggingId)} />}
          </DragOverlay>
        </div>
      </TabPanel>
      { event.hasWaitlistEnabled &&
        <TabPanel className={classNames('mx-3', styles.tabPanel)} selectedClassName={styles.activeTabPanel}>
          <div className={styles.studentsList}>
            {waitlistedStudentsFiltered.length !== 0 || searchWaitlistValue ?
              renderStudents(waitlistedStudentsFiltered) :
              <div className={classNames('d-flex flex-column align-items-center', styles.studentsPlaceholder)}>
                <ClapIcon className={styles.clapIcon}/>
                <p className={styles.placeholderTitle}>All Golfers are Paired</p>
                <p className={styles.placeholderMessage}>All waitlisted golfers have been paired or moved to another status.</p>
              </div>
            }
            <DragOverlay>
              {draggingId !== -1 && <StudentCard dragging student={eventStudents.find(s => s.id === draggingId)}/>}
            </DragOverlay>
          </div>
        </TabPanel>
      }
      <TabPanel className={classNames('mx-3', styles.tabPanel)} selectedClassName={styles.activeTabPanel}>
        <div className={styles.studentsList}>
          {notAttendingStudentsFiltered.length !== 0 || searchNotAttendingValue ?
            renderStudents(notAttendingStudentsFiltered) :
            <div className={classNames('d-flex flex-column align-items-center', styles.studentsPlaceholder)}>
              <ClapIcon className={styles.clapIcon}/>
              <p className={styles.placeholderTitle}>All Golfers are Paired</p>
              <p className={styles.placeholderMessage}>All not attending golfers have been paired or moved to another status.</p>
            </div>
          }
          <DragOverlay>
            {draggingId !== -1 && <StudentCard dragging student={eventStudents.find(s => s.id === draggingId)}/>}
          </DragOverlay>
        </div>
      </TabPanel>
    </Tabs>
  )
}

PairingsStudents.propTypes = {
  event: PropTypes.object,
  draggingId: PropTypes.number,
  draggedIds: PropTypes.array,
}

export default PairingsStudents
